import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import FmeaTable from '../components/FmeaTable';
import { Files, FileText } from 'lucide-react';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { getStandardConfig } from '../constants/fmeaStandards';
import { ModuleInfoModal } from '../components/ModuleInfoModal';

const FMEAs: React.FC = () => {
    const { allData, masterFunctions, activeProject } = useAppContext();
    const location = useLocation();

    // Check for state passed from navigation to set the initial view
    const initialViewMode = location.state?.view === 'draft' ? 'draft' : 'active';
    const [viewMode, setViewMode] = useState<'active' | 'draft'>(initialViewMode);

    const [selectedFunction, setSelectedFunction] = useState<string>('all');
    // REMOVED local selectedProject

    // Get the current FMEA standard configuration for display
    const standardConfig = useMemo(() => {
        return getStandardConfig(allData.projectMetadata?.fmeaStandard);
    }, [allData.projectMetadata?.fmeaStandard]);

    const filteredItems = (allData.fmeaItems || []).filter(item => {
        if (!item) return false;
        const matchesFunction = selectedFunction === 'all' || item.function === selectedFunction;

        // More robust status handling:
        // - If status is explicitly set, use it
        // - If status is undefined/null, default to 'draft' for safety
        // - This ensures new items appear in draft until explicitly made active
        const itemStatus = item.status || 'draft';
        const matchesStatus = viewMode === 'active' ? itemStatus === 'active' : itemStatus === 'draft';

        let matchesProject = true;
        if (activeProject !== 'all') {
            const activeProjectNormalized = normalizeProjectName(activeProject);

            // 1. Direct Project check (Metadata Standards v2.0.0)
            if (item.projectName) {
                matchesProject = normalizeProjectName(item.projectName) === activeProjectNormalized;
            } else {
                // 2. Fallback: Check if item's epoch belongs to the active project
                const itemEpoch = allData.iterationHistory?.find(e =>
                    (e.epochNumber || e.iteration) === item.epochId
                );

                if (itemEpoch?.epochName || itemEpoch?.iterationName) {
                    const name = itemEpoch.epochName || itemEpoch.iterationName || '';
                    const epochProjectName = extractProjectName(name);
                    matchesProject = normalizeProjectName(epochProjectName) === activeProjectNormalized;
                } else {
                    matchesProject = false;
                }
            }
        }

        return matchesFunction && matchesStatus && matchesProject;
    });

    // Calculate counts for both views (respecting project filter)
    const activeCount = (allData.fmeaItems || []).filter(item => {
        if (!item) return false;
        const itemStatus = item.status || 'draft';
        if (itemStatus !== 'active') return false;

        // Apply project filter
        if (activeProject !== 'all') {
            const activeProjectNormalized = normalizeProjectName(activeProject);

            if (item.projectName) {
                return normalizeProjectName(item.projectName) === activeProjectNormalized;
            } else {
                const itemEpoch = allData.iterationHistory?.find(e =>
                    (e.epochNumber || e.iteration) === item.epochId
                );

                if (itemEpoch?.epochName || itemEpoch?.iterationName) {
                    const name = itemEpoch.epochName || itemEpoch.iterationName || '';
                    const epochProjectName = extractProjectName(name);
                    return normalizeProjectName(epochProjectName) === activeProjectNormalized;
                }
                return false;
            }
        }
        return true;
    }).length;

    const draftCount = (allData.fmeaItems || []).filter(item => {
        if (!item) return false;
        const itemStatus = item.status || 'draft';
        if (itemStatus !== 'draft') return false;

        // Apply project filter
        if (activeProject !== 'all') {
            const activeProjectNormalized = normalizeProjectName(activeProject);

            if (item.projectName) {
                return normalizeProjectName(item.projectName) === activeProjectNormalized;
            } else {
                const itemEpoch = allData.iterationHistory?.find(e =>
                    (e.epochNumber || e.iteration) === item.epochId
                );

                if (itemEpoch?.epochName || itemEpoch?.iterationName) {
                    const name = itemEpoch.epochName || itemEpoch.iterationName || '';
                    const epochProjectName = extractProjectName(name);
                    return normalizeProjectName(epochProjectName) === activeProjectNormalized;
                }
                return false;
            }
        }
        return true;
    }).length;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-4 mb-1">
                        <h1 className="text-3xl font-bold text-text-primary">Failure Mode & Effects Analysis (FMEA)</h1>
                        {/* Specification Badge */}
                        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/10 to-cyan-500/10 border border-blue-500/30 rounded-lg">
                            <span className="text-xs text-text-secondary uppercase tracking-wide">Standard:</span>
                            <span className="text-sm font-bold text-blue-400">{standardConfig.name}</span>
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full">
                                {standardConfig.riskModel === 'AP' ? 'Action Priority' : 'RPN Model'}
                            </span>
                        </div>
                    </div>
                    <p className="text-text-secondary mt-1">Sorted by RPN. Click high/medium risk rows to jump to related DVP&R tests.</p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex bg-card border border-border rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('active')}
                            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-colors ${viewMode === 'active' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:bg-background'
                                }`}
                        >
                            <Files size={16} /> Active
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${viewMode === 'active' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
                                {activeCount}
                            </span>
                        </button>
                        <button
                            onClick={() => setViewMode('draft')}
                            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-colors ${viewMode === 'draft' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:bg-background'
                                }`}
                        >
                            <FileText size={16} /> Drafts
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${viewMode === 'draft' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
                                {draftCount}
                            </span>
                        </button>
                    </div>

                    <ActiveProjectBadge showStandard={false} />

                    <div className="flex items-center gap-2">
                        <label htmlFor="fmea-filter" className="text-sm font-medium text-text-secondary">Function:</label>
                        <select
                            id="fmea-filter"
                            value={selectedFunction}
                            onChange={(e) => setSelectedFunction(e.target.value)}
                            className="bg-card border border-border rounded-md px-3 py-1.5 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                            <option value="all">All Functions</option>
                            {masterFunctions.map(fn => (
                                <option key={fn} value={fn}>{fn}</option>
                            ))}
                        </select>
                    </div>

                    <ModuleInfoModal
                        title="FMEA Viewer"
                        description={`Browse and review all Failure Mode & Effects Analysis items for your ${activeProject !== 'all' ? 'selected project' : 'projects'}. This viewer is configured for ${standardConfig.name} and uses ${standardConfig.riskModel === 'RPN' ? 'Risk Priority Number (RPN)' : 'Action Priority (AP)'} methodology to assess risk levels.`}
                        description2={standardConfig.riskModel === 'RPN'
                            ? "Items are ranked by RPN (Severity × Occurrence × Detection). Higher RPN values indicate greater risk. Critical items typically have RPN > 100 or Severity ≥ 8."
                            : "Items are categorized by Action Priority: High (H), Medium (M), or Low (L). High-priority items require immediate action and detailed validation planning."
                        }
                        benefits={[
                            `Quick access to all failure modes across ${masterFunctions.length} system functions`,
                            `Filter by Draft vs Active status to review work-in-progress or released FMEAs`,
                            `Project-specific filtering ensures you see only relevant items for your current work`,
                            `${standardConfig.riskModel === 'RPN' ? 'Visual RPN indicators' : 'Color-coded Action Priority levels'} help prioritize high-risk items immediately`,
                            `Integrated with DVP&R: click items to jump to related test plans`,
                            `Standard-compliant: follows ${standardConfig.name} risk assessment criteria`
                        ]}
                        usageInstructions={[
                            `Toggle between "Active" (released) and "Drafts" (work-in-progress) tabs to view different item statuses`,
                            `Use the Function dropdown to filter items by system function or view all at once`,
                            `The project badge shows your current project filter - change it from the dashboard if needed`,
                            `Click any FMEA ID to view full details and change history in the FMEA Editor`,
                            `${standardConfig.riskModel === 'RPN' ? 'Look for red/yellow highlighting on high RPN items (>100)' : 'Focus on items marked with High (H) Action Priority first'}`,
                            `Use this view for team reviews, audits, and identifying areas needing additional controls`
                        ]}
                    />
                </div>
            </div>


            {filteredItems.length === 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center mb-6">
                    <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                        No {viewMode === 'active' ? 'Active' : 'Draft'} FMEA Items Found
                    </h3>
                    <p className="text-yellow-700 dark:text-yellow-300 mb-2">
                        {viewMode === 'active' ? (
                            <>
                                All FMEA items are currently in <strong>Draft</strong> status.
                                Switch to the <strong>Drafts</strong> tab ({draftCount} items) to view them.
                            </>
                        ) : (
                            <>No draft FMEA items found for the current project and function filter.</>
                        )}
                    </p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        {viewMode === 'active' ?
                            'To promote items to Active: Open FMEA Editor, edit an item, and change its status to "active".' :
                            'Try selecting "All Functions" or switching to a different project.'
                        }
                    </p>
                </div>
            )}

            <FmeaTable
                title={viewMode === 'active' ? "System FMEA (Released)" : "System FMEA (Drafts)"}
                items={filteredItems}
            />
        </div>
    );
};

export default FMEAs;
